import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { ExternalLink, Lock, Unlock, RefreshCw, Loader2 } from 'lucide-react';
import { MedDefenseBox } from '../EfiMedPanel';
import { fmtData, GATEWAY, KIND, fmtMoeda, STATUS } from './disputeUtils';

const Linha = ({ rotulo, children }) => (
  <p><b className="text-gray-600">{rotulo}:</b> {children || '-'}</p>
);

// Detalhe que abre embaixo da linha da planilha
export const DisputeDetail = ({ d, agir, acting, notes, setNotes, onReload }) => (
  <div className="p-4 bg-amber-50/40 border-t space-y-3" data-testid={`dispute-detail-${d.id}`}>
    <div className="grid md:grid-cols-3 gap-x-6 gap-y-1 text-sm">
      <Linha rotulo="Gateway">{`${GATEWAY[d.gateway] || d.gateway} · ${KIND[d.kind] || d.kind}`}</Linha>
      <Linha rotulo="Resultado">
        <Badge className={STATUS[d.status]?.cls}>{STATUS[d.status]?.label || d.status}</Badge>
        {d.result_note ? <span className="ml-2 text-gray-600">{d.result_note}</span> : null}
      </Linha>
      <Linha rotulo="Valor">{fmtMoeda(d.amount, d.currency)}</Linha>
      <Linha rotulo="Pedido">{d.order_id || 'não localizado'}</Linha>
      <Linha rotulo="Aberta no gateway">{fmtData(d.opened_at)}</Linha>
      <Linha rotulo="Encerrada no gateway">{fmtData(d.closed_at_gateway)}</Linha>
      <Linha rotulo="Motivo">{d.reason}</Linha>
      <Linha rotulo="WhatsApp">{d.whatsapp_tail ? `...${d.whatsapp_tail}` : null}</Linha>
      <Linha rotulo="EndToEnd / ID">{d.e2e_id || d.external_id}</Linha>
      {d.txid && <Linha rotulo="txid da cobrança">{d.txid}</Linha>}
      {(d.payer_name || d.payer_document) && (
        <p className="md:col-span-3 text-xs text-gray-600">
          <b>Pagador no Pix:</b> {d.payer_name || '-'}
          {d.payer_document ? ` · doc ${d.payer_document}` : ''}
          {d.payer_bank ? ` · ${d.payer_bank}` : ''}
        </p>
      )}
      {d.claim_text && (
        <p className="md:col-span-3 rounded bg-white border p-2 text-xs italic">
          <b className="not-italic">O que o cliente alegou:</b> “{d.claim_text}”
        </p>
      )}
      {d.analyst_note && (
        <p className="md:col-span-3 text-xs text-gray-600">
          <b>Resposta do analista:</b> {d.analyst_note}
        </p>
      )}
      {(d.protocol || (d.ticket || []).length > 0) && (
        <p className="md:col-span-3 text-xs text-gray-500">
          {d.protocol ? `Protocolo ${d.protocol}` : ''}
          {(d.ticket || []).length > 0 ? ` · ticket ${d.ticket.join(', ')}` : ''}
        </p>
      )}
      {d.deadline && d.status === 'open' && (
        <p className="md:col-span-3 text-amber-800 font-medium">
          Prazo para responder: {fmtData(d.deadline)}
          {d.evidence_submitted ? ' · defesa já enviada ao gateway' : ''}
        </p>
      )}
      {d.gateway_url && (
        <a href={d.gateway_url} target="_blank" rel="noopener noreferrer"
           data-testid={`dispute-gateway-link-${d.id}`}
           className="md:col-span-3 inline-flex items-center gap-1 text-blue-600 underline text-xs">
          abrir no painel do gateway <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>

    {d.gateway === 'efi' && d.status === 'open' && <MedDefenseBox dispute={d} onDone={onReload} />}

    {(d.history || []).length > 0 && (
      <div className="rounded-lg bg-white border p-2 space-y-1">
        {d.history.slice(-8).map((h, i) => (
          <p key={i} className="text-xs text-gray-600">
            {fmtData(h.at)} · <b>{STATUS[h.action]?.label || h.action}</b>
            {h.actor ? ` · ${h.actor}` : ''}{h.note ? ` · ${h.note}` : ''}
          </p>
        ))}
      </div>
    )}

    <div className="flex flex-wrap items-center gap-2">
      <Input className="max-w-xs h-9" placeholder="Motivo (fica no histórico)"
             data-testid={`dispute-note-${d.id}`} value={notes[d.id] || ''}
             onChange={(e) => setNotes({ ...notes, [d.id]: e.target.value })} />
      {d.blocked_now ? (
        <Button size="sm" variant="outline" data-testid={`dispute-release-${d.id}`}
                disabled={acting === `release-${d.id}`} onClick={() => agir(d.id, 'release')}>
          <Unlock className="h-4 w-4 mr-1" /> Liberar cliente
        </Button>
      ) : (
        <Button size="sm" variant="outline" data-testid={`dispute-block-${d.id}`}
                disabled={acting === `block-${d.id}`} onClick={() => agir(d.id, 'block')}>
          <Lock className="h-4 w-4 mr-1" /> Bloquear de novo
        </Button>
      )}
      {d.admin_released && (
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700"
              data-testid={`dispute-admin-released-${d.id}`}>
          <Unlock className="h-3 w-3" /> liberado pelo admin · este MED não bloqueia de novo (só um MED novo)
        </span>
      )}
      {d.gateway !== 'efi' && (
        <Button size="sm" variant="ghost" data-testid={`dispute-sync-${d.id}`}
                disabled={acting === `refresh-${d.id}`} onClick={() => agir(d.id, 'refresh')}>
          {acting === `refresh-${d.id}`
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <RefreshCw className="h-4 w-4 mr-1" />} Reconsultar no gateway
        </Button>
      )}
    </div>
  </div>
);

export default DisputeDetail;
